import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type CampEventSettingsDTO = {
  packageTemplateIds?: string[];
  packageTemplateId?: string | null;
  defaultStudentGroupId?: string | null;
};

export type CampSellPackageOptionDTO = {
  templateId: string;
  templateName: string;
  totalSessions: number;
  validDays: number;
  selectedRuleIds: string[];
  classTypeNames: string[];
};

export type CampEventDTO = {
  id: string;
  slug: string;
  title: string;
  status: "draft" | "open" | "closed";
  opensAt: string | null;
  closesAt: string | null;
  minAge: number;
  maxAge: number;
  ageReferenceDate: string;
  settings?: CampEventSettingsDTO;
};

export type CampEventClassOptionDTO = {
  id: string;
  eventId: string;
  classTypeId: string;
  classTypeName?: string;
  sortOrder: number;
  enabled: boolean;
  publicLabelLao: string | null;
  publicDescription: string | null;
  capacity?: number;
  waitlistEnabled?: boolean;
  waitlistCapacity?: number | null;
  lowThreshold?: number;
  criticalThreshold?: number;
  claimed?: number;
  remaining?: number;
  availability?: string;
  waitlistActiveCount?: number;
};

export type CampWaitlistEntryDTO = {
  id: string;
  eventId: string;
  classTypeId: string;
  className: string;
  labelLao: string | null;
  fullName: string;
  nickname: string;
  phoneNumber: string;
  status: string;
  position: number;
  offeredAt: string | null;
  offerExpiresAt: string | null;
  joinedAt: string;
  whatsappUrl: string | null;
};

export type CampRegistrationListItemDTO = {
  id: string;
  fullName: string;
  nickname: string;
  phoneNumber: string;
  status: string;
  submittedAt: string;
  assignedTo: string | null;
  classes: Array<{ classTypeId: string; name: string }>;
};

export type CampSimilarStudentMatchDTO = {
  id: string;
  memberId: string;
  name: string;
  phoneNumber: string | null;
  similarityPercent: number;
};

export type CampRegistrationDetailDTO = {
  id: string;
  eventId: string;
  fullName: string;
  nickname: string;
  dateOfBirth: string;
  gender: string;
  genderOther: string | null;
  phoneNumber: string;
  status: string;
  adminNotes: string | null;
  submittedAt: string;
  studentId: string | null;
  age: number | null;
  previewStudentName: string;
  classes: Array<{ classTypeId: string; name: string }>;
  event: {
    id: string;
    title: string;
    slug: string;
    settings?: CampEventSettingsDTO | null;
  } | null;
  studentPackageId?: string | null;
  packageSoldAt?: string | null;
};

export const campRegistrationsApi = {
  listEvents: () =>
    fetcher.get<{ items: CampEventDTO[] }>(
      `${config.apiUrl}/camp-registrations/events`,
    ),

  initializeSummerCamp2026: () =>
    fetcher.post<{
      eventId: string;
      slug: string;
      eventCreated: boolean;
      classOptionsCount: number;
    }>(`${config.apiUrl}/camp-registrations/events/initialize-summer-camp-2026`),

  getEvent: (id: string) =>
    fetcher.get<
      CampEventDTO & { classOptions: CampEventClassOptionDTO[] }
    >(`${config.apiUrl}/camp-registrations/events/${id}`),

  updateEvent: (
    id: string,
    body: Partial<CampEventDTO> & { settings?: CampEventSettingsDTO },
  ) =>
    fetcher.put<CampEventDTO>(
      `${config.apiUrl}/camp-registrations/events/${id}`,
      body,
    ),

  updateEventClasses: (
    id: string,
    options: Array<{
      classTypeId: string;
      sortOrder: number;
      enabled: boolean;
      publicLabelLao?: string | null;
      publicDescription?: string | null;
      capacity?: number;
      waitlistEnabled?: boolean;
      waitlistCapacity?: number | null;
      lowThreshold?: number;
      criticalThreshold?: number;
    }>,
  ) =>
    fetcher.put<{ items: CampEventClassOptionDTO[] }>(
      `${config.apiUrl}/camp-registrations/events/${id}/classes`,
      { options },
    ),

  updateClassOption: (
    eventId: string,
    optionId: string,
    body: {
      capacity?: number;
      waitlistEnabled?: boolean;
      waitlistCapacity?: number | null;
      lowThreshold?: number;
      criticalThreshold?: number;
    },
  ) =>
    fetcher.patch<CampEventClassOptionDTO>(
      `${config.apiUrl}/camp-registrations/events/${eventId}/class-options/${optionId}`,
      body,
    ),

  listWaitlist: (eventId: string, classTypeId?: string) => {
    const url = new URL(
      `${config.apiUrl}/camp-registrations/events/${eventId}/waitlist`,
    );
    if (classTypeId) url.searchParams.set("classTypeId", classTypeId);
    return fetcher.get<{ items: CampWaitlistEntryDTO[] }>(url.toString());
  },

  promoteWaitlist: (id: string) =>
    fetcher.post(`${config.apiUrl}/camp-registrations/waitlist/${id}/promote`),

  skipWaitlist: (id: string) =>
    fetcher.post(`${config.apiUrl}/camp-registrations/waitlist/${id}/skip`),

  listRegistrations: (query: OffsetPageQueryDTO & {
    eventId?: string;
    status?: string;
    classTypeId?: string;
    search?: string;
  }) => {
    const url = new URL(`${config.apiUrl}/camp-registrations/registrations`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.eventId) url.searchParams.set("eventId", query.eventId);
    if (query.status) url.searchParams.set("status", query.status);
    if (query.classTypeId) url.searchParams.set("classTypeId", query.classTypeId);
    if (query.search) url.searchParams.set("search", query.search);
    return fetcher.get<{
      data: CampRegistrationListItemDTO[];
      meta: { total: number; offset: number; limit: number };
    }>(url.toString());
  },

  getRegistration: (id: string) =>
    fetcher.get<CampRegistrationDetailDTO>(
      `${config.apiUrl}/camp-registrations/registrations/${id}`,
    ),

  updateRegistration: (
    id: string,
    body: {
      status?: string;
      adminNotes?: string | null;
      assignedTo?: string | null;
    },
  ) =>
    fetcher.patch(
      `${config.apiUrl}/camp-registrations/registrations/${id}`,
      body,
    ),

  getSimilarStudents: (id: string) =>
    fetcher.get<{
      ok: true;
      matches: CampSimilarStudentMatchDTO[];
    }>(
      `${config.apiUrl}/camp-registrations/registrations/${id}/similar-students`,
    ),

  convertRegistration: async (
    id: string,
    body: {
      linkExistingStudentId?: string;
      groupId?: string | null;
      additionalNotes?: string | null;
      forceCreateNew?: boolean;
    },
  ) => {
    const res = await fetch(
      `${config.apiUrl}/camp-registrations/registrations/${id}/convert`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );

    const data = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      studentId?: string;
      memberId?: string;
      linked?: boolean;
      code?: string;
      matches?: CampSimilarStudentMatchDTO[];
      message?: string;
      error?: string;
    };

    if (!res.ok) {
      if (
        res.status === 409 &&
        (data.code === "SIMILAR_STUDENTS" || data.code === "DUPLICATE_PHONE") &&
        data.matches
      ) {
        const err = new Error(data.code) as Error & {
          code: string;
          matches: CampSimilarStudentMatchDTO[];
        };
        err.code = data.code;
        err.matches = data.matches;
        throw err;
      }
      throw new Error(data.message || data.error || "convert failed");
    }

    return data;
  },

  exportCsv: (eventId: string) =>
    `${config.apiUrl}/camp-registrations/registrations/export.csv?eventId=${encodeURIComponent(eventId)}`,

  getSellPackageOptions: (id: string) =>
    fetcher.get<{
      ok: true;
      campRegistrationId: string;
      studentId: string;
      templates: CampSellPackageOptionDTO[];
    }>(
      `${config.apiUrl}/camp-registrations/registrations/${id}/sell-package-options`,
    ),

  getSellPackagePrefill: (id: string, templateId?: string) => {
    const url = new URL(
      `${config.apiUrl}/camp-registrations/registrations/${id}/sell-package-prefill`,
    );
    if (templateId) url.searchParams.set("templateId", templateId);
    return fetcher.get<{
      ok: true;
      campRegistrationId: string;
      studentId: string;
      templateId: string;
      templateName: string;
      selectedRuleIds: string[];
      classTypeNames: string[];
      templates: CampSellPackageOptionDTO[];
    }>(url.toString());
  },

  linkPackage: (id: string, studentPackageId: string) =>
    fetcher.post(
      `${config.apiUrl}/camp-registrations/registrations/${id}/link-package`,
      { studentPackageId },
    ),
};
